import { useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import '../Roles/roles.css';
import { PermissionV2 } from '../../../types/role';
import { OrganizationRolePayload, OrganizationRoleV2 } from '../../../types/organizationTeam';
import { RoleForm } from '../Roles/RoleForm';
import { groupPermissions } from '../Roles/groupPermissions';

interface TeamRoleDialogProps {
  role: OrganizationRoleV2 | null; // null = create
  permissions: PermissionV2[];
  onClose: () => void;
  onSubmit: (payload: OrganizationRolePayload) => Promise<void>;
  isSubmitting: boolean;
  errorMessage?: string;
}

/**
 * Create/edit one of an organization's roles with the same grouped picker as
 * platform roles. Only org-grantable permissions are passed in, so nothing
 * here can offer a permission the server would refuse.
 */
export const TeamRoleDialog = ({
  role,
  permissions,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}: TeamRoleDialogProps) => {
  const { t } = useTranslation();
  const groups = useMemo(() => groupPermissions(permissions), [permissions]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {role ? t('dashboard.team.roles.editTitle') : t('dashboard.team.roles.createTitle')}
      </DialogTitle>
      <DialogContent>
        <p className="role-form-hint" style={{ marginTop: 0 }}>
          {t('dashboard.team.roles.permissionsHint')}
        </p>
        <RoleForm
          initial={role}
          permissionGroups={groups}
          onSubmit={(payload) => onSubmit(payload)}
          onCancel={onClose}
          isSubmitting={isSubmitting}
          errorMessage={errorMessage}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TeamRoleDialog;
